/**
 * Hand-placed orders, out through the same StockIntel session the bots use —
 * a token allows one connection, so there is no second socket.
 *
 * MARKET or LIMIT, and DAY only. STOP is not offered because nothing on our
 * side watches a trigger, and GTC is not on the plan; an order that looked like
 * it would rest overnight and was silently cancelled at the close would be
 * worse than not offering it. IOC is left out for now.
 *
 * These are REAL orders for real money. The server gates them on the same
 * allowlist as the bots and refuses a limit more than 20% from the last trade.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import type { OrderSide } from '@tradekaro/shared';
import { Card } from '../../components/UI';
import { Icon } from '../../components/Icons';
import { placeBrokerOrder } from '../../lib/bots';
import { ApiClientError } from '../../lib/api';

type TicketType = 'MARKET' | 'LIMIT';

interface StockLite {
  sym: string;
  price: number;
}

interface Props {
  stock: StockLite;
  onPlace?: () => void;
}

type Stage = 'FORM' | 'CONFIRM' | 'SUCCESS';

export const OrderTicket = ({ stock, onPlace }: Props) => {
  const [side, setSide] = useState<OrderSide>('BUY');
  const [orderType, setOrderType] = useState<TicketType>('LIMIT');
  const [qty, setQty] = useState(100);
  const [limitPrice, setLimitPrice] = useState(stock.price);
  const [stage, setStage] = useState<Stage>('FORM');

  const isBuy = side === 'BUY';
  const isMarket = orderType === 'MARKET';
  const effectivePrice = isMarket ? stock.price : limitPrice;
  const total = qty * effectivePrice;
  const fee = 0; // Fees disabled in demo per user decision
  const netTotal = isBuy ? total + fee : total - fee;
  const [orderId, setOrderId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submitTrade = async () => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const r = await placeBrokerOrder({
        symbol: stock.sym,
        side,
        type: orderType,
        quantity: qty,
        // A market order carries no price; the exchange decides it.
        price: isMarket ? undefined : limitPrice,
      });
      setOrderId(r.requestId);
      setStage('SUCCESS');
      setTimeout(() => {
        setStage('FORM');
        setOrderId(null);
        onPlace?.();
      }, 2400);
    } catch (e) {
      if (e instanceof ApiClientError) setSubmitError(e.message);
      else setSubmitError('Network error — sign in and try again');
      setStage('FORM');
    } finally {
      setSubmitting(false);
    }
  };

  const sideColor = isBuy ? 'var(--tk-up)' : 'var(--tk-down)';
  const sideSoft = isBuy ? 'var(--tk-up-soft)' : 'var(--tk-down-soft)';

  return (
    <Card padding={20} style={{ position: 'sticky', top: 110 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 14,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Place order</h3>
        <span
          style={{
            fontSize: 10.5,
            color: 'var(--tk-ink-3)',
            fontFamily: 'var(--tk-mono)',
            letterSpacing: '0.06em',
          }}
        >
          {stock.sym} · LIVE
        </span>
      </div>

      {stage === 'FORM' && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 0,
              background: 'var(--tk-surface-2)',
              borderRadius: 'var(--tk-radius-sm)',
              border: '1px solid var(--tk-line)',
              padding: 4,
              marginBottom: 16,
            }}
          >
            {(['BUY', 'SELL'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setSide(s)}
                style={{
                  padding: '11px 0',
                  fontSize: 13.5,
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: 7,
                  background:
                    side === s ? (s === 'BUY' ? 'var(--tk-up)' : 'var(--tk-down)') : 'transparent',
                  color: side === s ? 'var(--tk-accent-ink)' : 'var(--tk-ink-3)',
                  transition: 'all 0.18s var(--tk-ease)',
                }}
              >
                {s}
              </button>
            ))}
          </div>

          <FieldLabel>Order type</FieldLabel>
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            {(['MARKET', 'LIMIT'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setOrderType(t)}
                style={{
                  flex: 1,
                  padding: '8px 0',
                  fontSize: 12.5,
                  fontWeight: 600,
                  fontFamily: 'var(--tk-mono)',
                  letterSpacing: '0.04em',
                  border: '1px solid var(--tk-line)',
                  borderRadius: 'var(--tk-radius-sm)',
                  background: orderType === t ? 'var(--tk-ink)' : 'transparent',
                  color: orderType === t ? 'var(--tk-bg)' : 'var(--tk-ink-2)',
                  cursor: 'pointer',
                  transition: 'all 0.15s var(--tk-ease)',
                }}
              >
                {t}
              </button>
            ))}
          </div>

          <FieldLabel>Quantity</FieldLabel>
          <NumberInput value={qty} onChange={setQty} suffix="shares" step={10} />
          <div style={{ display: 'flex', gap: 6, marginTop: 8, marginBottom: 14 }}>
            {[25, 50, 75, 100].map((p) => (
              <button
                key={p}
                onClick={() => setQty(Math.round(((10000 / stock.price) * p) / 100 / 10) * 10)}
                style={{
                  flex: 1,
                  padding: '6px 0',
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: 'var(--tk-mono)',
                  border: '1px solid var(--tk-line)',
                  borderRadius: 6,
                  background: 'transparent',
                  color: 'var(--tk-ink-3)',
                  cursor: 'pointer',
                }}
              >
                {p}%
              </button>
            ))}
          </div>

          {!isMarket && (
            <>
              <FieldLabel>Limit price</FieldLabel>
              <NumberInput
                value={limitPrice}
                onChange={setLimitPrice}
                prefix="PKR"
                step={0.05}
                decimals={2}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11.5,
                  color: 'var(--tk-ink-3)',
                  marginTop: 6,
                  marginBottom: 14,
                  fontFamily: 'var(--tk-mono)',
                }}
              >
                <span>Mkt {stock.price.toFixed(2)}</span>
                <span>{(((limitPrice - stock.price) / stock.price) * 100).toFixed(2)}% from market</span>
              </div>
            </>
          )}

          <FieldLabel>Time in force</FieldLabel>
          {/* One choice, shown rather than hidden: an order that does not fill
              today is cancelled by the exchange at the close, and that is worth
              knowing before placing it. GTC is not on the plan. */}
          <div
            title="Day order. Anything unfilled is cancelled by the exchange at the close — GTC is not available on this plan."
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 18,
              padding: '8px 12px',
              border: '1px solid var(--tk-line)',
              borderRadius: 6,
              background: 'var(--tk-surface-2)',
              fontSize: 11.5,
              fontFamily: 'var(--tk-mono)',
            }}
          >
            <span style={{ fontWeight: 700, letterSpacing: '0.04em' }}>DAY</span>
            <span style={{ color: 'var(--tk-ink-3)' }}>cancelled at the close if unfilled</span>
          </div>

          <div
            style={{
              padding: '14px 16px',
              background: 'var(--tk-surface-2)',
              borderRadius: 'var(--tk-radius-md)',
              marginBottom: 16,
            }}
          >
            <SummaryRow label="Estimated total" value={total} prefix="PKR " />
            <SummaryRow label="Brokerage & taxes" value={fee} prefix="PKR " muted />
            <div style={{ height: 1, background: 'var(--tk-line)', margin: '8px 0' }} />
            <SummaryRow
              label={isBuy ? "You'll pay" : "You'll receive"}
              value={netTotal}
              prefix="PKR "
              bold
            />
          </div>

          <button
            onClick={() => setStage('CONFIRM')}
            style={{
              width: '100%',
              padding: 14,
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: '0.03em',
              fontFamily: 'var(--tk-sans)',
              background: sideColor,
              color: 'var(--tk-accent-ink)',
              border: 'none',
              borderRadius: 'var(--tk-radius-sm)',
              cursor: 'pointer',
              transition: 'all 0.15s var(--tk-ease)',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
            onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
          >
            Review {side === 'BUY' ? 'buy' : 'sell'} order
          </button>

          <div
            style={{
              marginTop: 14,
              padding: '10px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 11.5,
              color: 'var(--tk-ink-3)',
              background: 'var(--tk-bg)',
              borderRadius: 8,
              border: '1px dashed var(--tk-line)',
            }}
          >
            <Icon name="wallet" size={13} />
            <span>
              Available cash:{' '}
              <strong style={{ color: 'var(--tk-ink-2)', fontFamily: 'var(--tk-mono)' }}>
                PKR 84,210
              </strong>
            </span>
          </div>
        </>
      )}

      {stage === 'CONFIRM' && (
        <div className="tk-fade-in">
          <div
            style={{
              marginBottom: 16,
              padding: '4px 12px',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              color: sideColor,
              background: sideSoft,
              borderRadius: 4,
              display: 'inline-block',
            }}
          >
            REVIEW {side}
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4 }}>
            {qty.toLocaleString()} {stock.sym}
          </div>
          <div style={{ fontSize: 13.5, color: 'var(--tk-ink-3)', marginBottom: 22 }}>
            @ {isMarket ? 'market' : `PKR ${limitPrice.toFixed(2)}`} · {orderType} · DAY
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              paddingBottom: 18,
              borderBottom: '1px solid var(--tk-line)',
            }}
          >
            <ConfirmRow label="Order type" value={`${orderType} · DAY`} />
            <ConfirmRow label="Quantity" value={`${qty.toLocaleString()} shares`} />
            <ConfirmRow
              label="Price"
              value={isMarket ? 'Market' : `PKR ${limitPrice.toFixed(2)}`}
            />
            <ConfirmRow
              label="Estimated total"
              value={`PKR ${total.toLocaleString('en-PK', { maximumFractionDigits: 2 })}`}
            />
            <ConfirmRow
              label="Brokerage & taxes"
              value={`PKR ${fee.toLocaleString('en-PK', { maximumFractionDigits: 2 })}`}
              muted
            />
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              padding: '18px 0',
            }}
          >
            <span style={{ fontSize: 13, color: 'var(--tk-ink-2)' }}>
              {isBuy ? "You'll pay" : "You'll receive"}
            </span>
            <span
              className="mono"
              style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}
            >
              PKR {netTotal.toLocaleString('en-PK', { maximumFractionDigits: 2 })}
            </span>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => setStage('FORM')}
              style={{
                flex: 1,
                padding: 13,
                fontSize: 13.5,
                fontWeight: 600,
                background: 'transparent',
                color: 'var(--tk-ink-2)',
                border: '1px solid var(--tk-line)',
                borderRadius: 'var(--tk-radius-sm)',
                cursor: 'pointer',
              }}
            >
              Back
            </button>
            <button
              onClick={submitTrade}
              disabled={submitting}
              style={{
                flex: 2,
                padding: 13,
                fontSize: 13.5,
                fontWeight: 700,
                letterSpacing: '0.03em',
                background: sideColor,
                color: 'var(--tk-accent-ink)',
                border: 'none',
                borderRadius: 'var(--tk-radius-sm)',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1,
              }}
            >
              {submitting ? 'Placing…' : `Confirm ${side.toLowerCase()}`}
            </button>
          </div>
        </div>
      )}

      {stage === 'SUCCESS' && (
        <div className="tk-fade-in" style={{ textAlign: 'center', padding: '24px 0' }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 999,
              margin: '0 auto 18px',
              background: sideSoft,
              color: sideColor,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 700 }}>Order placed</h3>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--tk-ink-3)' }}>
            {side === 'BUY' ? 'Buy' : 'Sell'} {qty} {stock.sym} ·{' '}
            {isMarket ? 'Market' : `Limit ${limitPrice.toFixed(2)}`}
          </p>
          <p
            style={{
              marginTop: 16,
              fontSize: 11.5,
              color: 'var(--tk-ink-3)',
              fontFamily: 'var(--tk-mono)',
            }}
          >
            ORD-{orderId ? orderId.slice(0, 8).toUpperCase() : '----'}
          </p>
        </div>
      )}

      {submitError && stage === 'FORM' && (
        <div
          style={{
            marginTop: 12,
            padding: '10px 12px',
            borderRadius: 'var(--tk-radius-sm)',
            background: 'var(--tk-down-soft)',
            color: 'var(--tk-down)',
            fontSize: 12.5,
            fontWeight: 600,
          }}
        >
          {submitError}
        </div>
      )}
    </Card>
  );
};

const FieldLabel = ({ children }: { children: ReactNode }) => (
  <div
    style={{
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'var(--tk-ink-3)',
      marginBottom: 6,
    }}
  >
    {children}
  </div>
);

interface NumberInputProps {
  value: number;
  onChange: (v: number) => void;
  prefix?: string;
  suffix?: string;
  step?: number;
  decimals?: number;
}

const NumberInput = ({
  value,
  onChange,
  prefix,
  suffix,
  step = 1,
  decimals = 0,
}: NumberInputProps) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      background: 'var(--tk-surface-2)',
      border: '1px solid var(--tk-line)',
      borderRadius: 'var(--tk-radius-sm)',
      padding: '0 4px',
      transition: 'border-color 0.15s var(--tk-ease)',
    }}
  >
    <button
      onClick={() => onChange(Math.max(0, +(value - step).toFixed(decimals)))}
      style={{
        width: 32,
        height: 38,
        border: 'none',
        background: 'transparent',
        color: 'var(--tk-ink-2)',
        cursor: 'pointer',
        fontSize: 18,
        fontWeight: 500,
      }}
    >
      −
    </button>
    {prefix && (
      <span style={{ fontSize: 11, color: 'var(--tk-ink-3)', fontFamily: 'var(--tk-mono)' }}>
        {prefix}
      </span>
    )}
    <input
      type="text"
      value={typeof value === 'number' ? value.toFixed(decimals) : value}
      onChange={(e) => {
        const v = parseFloat(e.target.value);
        if (!Number.isNaN(v)) onChange(v);
      }}
      style={{
        flex: 1,
        height: 38,
        border: 'none',
        background: 'transparent',
        textAlign: 'center',
        fontSize: 15,
        fontWeight: 600,
        fontFamily: 'var(--tk-mono)',
        color: 'var(--tk-ink)',
        outline: 'none',
        minWidth: 0,
      }}
    />
    {suffix && <span style={{ fontSize: 11, color: 'var(--tk-ink-3)' }}>{suffix}</span>}
    <button
      onClick={() => onChange(+(value + step).toFixed(decimals))}
      style={{
        width: 32,
        height: 38,
        border: 'none',
        background: 'transparent',
        color: 'var(--tk-ink-2)',
        cursor: 'pointer',
        fontSize: 18,
        fontWeight: 500,
      }}
    >
      +
    </button>
  </div>
);

const SummaryRow = ({
  label,
  value,
  prefix = '',
  muted,
  bold,
}: {
  label: string;
  value: number;
  prefix?: string;
  muted?: boolean;
  bold?: boolean;
}) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      padding: '4px 0',
    }}
  >
    <span style={{ fontSize: 12.5, color: muted ? 'var(--tk-ink-3)' : 'var(--tk-ink-2)' }}>
      {label}
    </span>
    <span
      className="mono"
      style={{
        fontSize: bold ? 15 : 13,
        fontWeight: bold ? 700 : 500,
        color: muted ? 'var(--tk-ink-3)' : 'var(--tk-ink)',
      }}
    >
      {prefix}
      {value.toLocaleString('en-PK', { maximumFractionDigits: 2 })}
    </span>
  </div>
);

const ConfirmRow = ({ label, value, muted }: { label: string; value: string; muted?: boolean }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
    <span style={{ fontSize: 12.5, color: 'var(--tk-ink-3)' }}>{label}</span>
    <span
      className="mono"
      style={{
        fontSize: 13,
        fontWeight: muted ? 500 : 600,
        color: muted ? 'var(--tk-ink-3)' : 'var(--tk-ink)',
      }}
    >
      {value}
    </span>
  </div>
);
